import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Cog6ToothIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  CreditCardIcon,
  Bars3Icon,
  WifiIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { usePWAStore } from '../../store/pwaStore';
import { SettingsModal } from '../modals/SettingsModal';
import { FeedbackModal } from '../modals/FeedbackModal';
import { useTranslation } from 'react-i18next';

// Map routes to feature area IDs for feedback pre-selection
const routeToFeatureArea: Record<string, string> = {
  '/review-contract': 'review_documents',
  '/legal-questions': 'legal_questions',
  '/generate-will': 'generate_will',
  '/generate-bill-of-sale': 'bill_of_sale',
  '/legal-letters': 'legal_letters',
  '/name-change': 'name_change',
  '/small-claims': 'small_claims',
  '/tenant-rights': 'tenant_rights',
  '/employment-advisor': 'employment_advisor',
  '/deadline-calculator': 'deadline_calculator',
  '/contract-negotiation': 'contract_negotiation',
  '/legal-glossary': 'legal_glossary',
  '/ai-advisor': 'ai_advisor',
  '/business-formation': 'business_formation',
  '/trademark-search': 'trademark_search',
  '/business-contracts': 'business_contracts',
};

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearAuth, user } = useAuthStore();
  const {
    toggleMobileSidebar,
    isOffline,
    canInstall,
    promptInstall,
    updateAvailable,
    applyUpdate,
  } = usePWAStore();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  // Get current feature area based on route
  const currentFeatureArea = useMemo(() => {
    return routeToFeatureArea[location.pathname] || '';
  }, [location.pathname]);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  // Close account menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };

    if (isAccountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountMenuOpen]);

  return (
    <>
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-14 flex items-center justify-between px-4 lg:px-6 safe-area-inset">
        {/* Left side - Hamburger menu for mobile */}
        <div className="flex items-center lg:hidden">
          <button
            onClick={toggleMobileSidebar}
            className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Mobile logo */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center ml-2"
          >
            <img src="/scales-logo.png" alt="Auxilio Legal" className="h-8 w-8" />
          </button>
        </div>

        {/* Desktop: empty left side for balance */}
        <div className="hidden lg:block" />

        {/* Right side - Actions */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          {/* Offline indicator */}
          {isOffline && (
            <div className="flex items-center px-2 py-1 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg text-xs sm:text-sm">
              <WifiIcon className="h-4 w-4 mr-1 line-through" />
              <span className="hidden sm:inline">Offline</span>
            </div>
          )}

          {/* Update available indicator */}
          {updateAvailable && (
            <button
              onClick={applyUpdate}
              className="flex items-center px-2 py-1 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-xs sm:text-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Update</span>
            </button>
          )}

          {/* Install PWA button - only show if can install */}
          {canInstall && (
            <button
              onClick={promptInstall}
              className="flex items-center px-2 py-1 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 rounded-lg text-xs sm:text-sm hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
              title="Install App"
            >
              <ArrowDownTrayIcon className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Install</span>
            </button>
          )}

          {/* Settings */}
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
            title={t('common:sidebar.settings')}
          >
            <Cog6ToothIcon className="h-5 w-5" />
            <span className="hidden md:inline">{t('common:sidebar.settings')}</span>
          </button>

          {/* Feedback - hidden on small mobile */}
          <button
            onClick={() => setIsFeedbackOpen(true)}
            className="hidden sm:flex items-center space-x-2 px-2 sm:px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
            title={t('common:header.feedback')}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            <span className="hidden md:inline">{t('common:header.feedback')}</span>
          </button>

          {/* My Account with Dropdown */}
          <div className="relative" ref={accountMenuRef}>
            <button
              onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
              className="flex items-center space-x-2 px-2 sm:px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
              title={t('common:sidebar.account')}
            >
              <UserIcon className="h-5 w-5" />
              <span className="hidden md:inline">{t('common:sidebar.account')}</span>
            </button>

            {/* Account Dropdown Menu */}
            {isAccountMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                <button
                  onClick={() => {
                    setIsAccountMenuOpen(false);
                    navigate('/profile');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <UserCircleIcon className="h-5 w-5 mr-3" />
                  {t('common:header.profile')}
                </button>
                <button
                  onClick={() => {
                    setIsAccountMenuOpen(false);
                    navigate('/billing');
                  }}
                  className="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <CreditCardIcon className="h-5 w-5 mr-3" />
                  {t('common:header.billing')}
                </button>
                {/* Feedback in dropdown for mobile */}
                <button
                  onClick={() => {
                    setIsAccountMenuOpen(false);
                    setIsFeedbackOpen(true);
                  }}
                  className="flex sm:hidden items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
                  {t('common:header.feedback')}
                </button>
                {/* Logout in dropdown */}
                <div className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1">
                  <button
                    onClick={() => {
                      setIsAccountMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                    {t('common:buttons.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Logout button - hidden on mobile (moved to dropdown) */}
          <button
            onClick={handleLogout}
            className="hidden lg:flex items-center space-x-2 px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
            title={t('common:buttons.logout')}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            <span className="hidden md:inline">{t('common:buttons.logout')}</span>
          </button>
        </div>
      </header>

      {/* Settings Modal */}
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        currentFeatureArea={currentFeatureArea}
      />
    </>
  );
};
